import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  Users,
  Gift,
  Copy,
  Send,
  Trophy,
  Share2,
  Check,
  Mail,
  Linkedin,
  Twitter,
  ShoppingCart,
  ArrowRight
} from "lucide-react";

const ReferralPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [referralData, setReferralData] = useState(null);
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteMessage, setInviteMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferralData();
    fetchStats();
    fetchLeaderboard();
  }, []);

  const fetchReferralData = async () => {
    try {
      const response = await fetch(`${API}/referral/my-code`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setReferralData(data);
      }
    } catch (error) {
      console.error("Failed to fetch referral code:", error);
      toast.error("Failed to load referral data");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/referral/stats`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await fetch(`${API}/referral/leaderboard`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setLeaderboard(data.leaderboard || []);
      }
    } catch (error) {
      console.error("Failed to fetch leaderboard:", error);
    }
  };

  const copyToClipboard = async () => {
    if (referralData?.referral_link) {
      await navigator.clipboard.writeText(referralData.referral_link);
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const sendInvite = async () => {
    if (!inviteEmail || !inviteEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    setSending(true);
    try {
      const response = await fetch(`${API}/referral/send-invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: inviteEmail, message: inviteMessage }),
      });

      if (response.ok) {
        toast.success(`Invitation sent to ${inviteEmail}!`);
        setInviteEmail("");
        setInviteMessage("");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send invitation");
      }
    } catch (error) {
      toast.error("Failed to send invitation");
    } finally {
      setSending(false);
    }
  };

  const shareOnLinkedIn = () => {
    // Use LinkedIn's share URL with URL parameter
    const referralLink = referralData?.referral_link || "";
    const shareTitle = "Join me on Rekrutor - AI-powered recruitment platform!";
    const shareSummary = "Whether you're looking for your next career move or searching for top talent, Rekrutor uses AI to create perfect matches. Sign up using my referral link and we both get rewards!";
    
    // LinkedIn share dialog - this is the reliable way to share
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}`;
    window.open(linkedInUrl, "_blank", "width=600,height=600");
  };

  const shareOnTwitter = () => {
    const url = encodeURIComponent(referralData?.referral_link || "");
    const text = encodeURIComponent("🚀 Join me on Rekrutor - the AI-powered recruitment platform! Sign up using my referral link and we both get rewards:");
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian">
        <Sidebar />
        <main className="lg:ml-64 p-4 sm:p-6 lg:p-8 pt-20 lg:pt-8">
          <div className="flex items-center justify-center h-64">
            <div className="w-16 h-16 bg-crimson/20 flex items-center justify-center animate-pulse">
              <Gift className="w-8 h-8 text-crimson" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      <Sidebar />
      <main className="lg:ml-64 p-4 sm:p-6 lg:p-8 pt-20 lg:pt-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">
            REFER & <span className="text-crimson">EARN</span>
          </h1>
          <p className="text-gray-400 text-sm sm:text-base">
            Invite friends to Rekrutor and earn rewards when they join!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Referral Code Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-obsidian-light border border-[#222] p-4 sm:p-6"
              data-testid="referral-code-card"
            >
              <h3 className="font-heading text-base sm:text-lg font-bold mb-3 sm:mb-4 flex items-center gap-2">
                <Share2 className="w-4 h-4 sm:w-5 sm:h-5 text-crimson" />
                Your Referral Code
              </h3>
              
              <div className="bg-[#111] border border-crimson/30 p-3 sm:p-4 mb-3 sm:mb-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <p className="text-gray-500 text-xs sm:text-sm mb-1">Your unique code</p>
                    <p className="text-xl sm:text-2xl font-mono font-bold text-crimson" data-testid="referral-code">
                      {referralData?.referral_code}
                    </p>
                  </div>
                  <Button
                    onClick={copyToClipboard}
                    variant="outline"
                    className="border-crimson text-crimson hover:bg-crimson hover:text-white text-sm w-full sm:w-auto"
                    data-testid="copy-referral-link-btn"
                  >
                    {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                    {copied ? "Copied!" : "Copy Link"}
                  </Button>
                </div>
              </div>

              {/* Social Share Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <Button
                  onClick={shareOnLinkedIn}
                  className="flex-1 bg-[#0077b5] hover:bg-[#006097] text-sm"
                  data-testid="share-linkedin-btn"
                >
                  <Linkedin className="w-4 h-4 mr-2" />
                  Share on LinkedIn
                </Button>
                <Button
                  onClick={shareOnTwitter}
                  className="flex-1 bg-[#1da1f2] hover:bg-[#0d8ddb] text-sm"
                  data-testid="share-twitter-btn"
                >
                  <Twitter className="w-4 h-4 mr-2" />
                  Share on Twitter
                </Button>
              </div>
            </motion.div>

            {/* Send Invite Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-obsidian-light border border-[#222] p-4 sm:p-6"
              data-testid="send-invite-card"
            >
              <h3 className="font-heading text-base sm:text-lg font-bold mb-3 sm:mb-4 flex items-center gap-2">
                <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-crimson" />
                Invite via Email
              </h3>

              <div className="space-y-3 sm:space-y-4">
                <div>
                  <label className="text-xs sm:text-sm text-gray-400 mb-1 block">Friend's Email</label>
                  <Input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="friend@example.com"
                    className="bg-[#111] border-[#333] text-white text-sm"
                    data-testid="invite-email-input"
                  />
                </div>
                <div>
                  <label className="text-xs sm:text-sm text-gray-400 mb-1 block">Personal Message (optional)</label>
                  <Input
                    value={inviteMessage}
                    onChange={(e) => setInviteMessage(e.target.value)}
                    placeholder="Hey! I think you'd love Rekrutor..."
                    className="bg-[#111] border-[#333] text-white text-sm"
                    data-testid="invite-message-input"
                  />
                </div>
                <Button
                  onClick={sendInvite}
                  disabled={sending || !inviteEmail}
                  className="w-full bg-crimson hover:bg-crimson-hover text-sm"
                  data-testid="send-invite-btn"
                >
                  {sending ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send Invitation
                </Button>
              </div>
            </motion.div>

            {/* Referred Users */}
            {stats?.referred_users?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-obsidian-light border border-[#222] p-4 sm:p-6"
              >
                <h3 className="font-heading text-base sm:text-lg font-bold mb-3 sm:mb-4 flex items-center gap-2">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 text-crimson" />
                  Your Referrals ({stats.referred_users.length})
                </h3>
                <div className="space-y-2 sm:space-y-3">
                  {stats.referred_users.map((referral, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-2 sm:p-3 bg-[#111] border border-[#222]"
                    >
                      <div className="min-w-0 flex-1 mr-2">
                        <p className="text-white font-medium text-sm sm:text-base truncate">{referral.name}</p>
                        <p className="text-gray-500 text-xs">
                          Joined {new Date(referral.joined_at).toLocaleDateString()}
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-1 flex-shrink-0 ${
                        referral.status === "converted" 
                          ? "bg-green-500/20 text-green-400" 
                          : "bg-yellow-500/20 text-yellow-400"
                      }`}>
                        {referral.status === "converted" ? "Converted" : "Pending"}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>

          {/* Sidebar Stats */}
          <div className="space-y-4 sm:space-y-6">
            {/* Stats Cards */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-obsidian-light border border-[#222] p-4 sm:p-6"
              data-testid="referral-stats"
            >
              <h3 className="font-heading text-base sm:text-lg font-bold mb-3 sm:mb-4">Your Stats</h3>
              <div className="grid grid-cols-2 lg:grid-cols-1 gap-2 sm:gap-4">
                <div className="flex justify-between items-center p-2 sm:p-3 bg-[#111]">
                  <span className="text-gray-400 text-xs sm:text-sm">Total Referrals</span>
                  <span className="text-lg sm:text-xl font-bold text-white">{stats?.total_referrals || 0}</span>
                </div>
                <div className="flex justify-between items-center p-2 sm:p-3 bg-[#111]">
                  <span className="text-gray-400 text-xs sm:text-sm">Successful</span>
                  <span className="text-lg sm:text-xl font-bold text-green-400">{stats?.successful_referrals || 0}</span>
                </div>
                <div className="flex justify-between items-center p-2 sm:p-3 bg-[#111]">
                  <span className="text-gray-400 text-xs sm:text-sm">Pending</span>
                  <span className="text-lg sm:text-xl font-bold text-yellow-400">{stats?.pending_referrals || 0}</span>
                </div>
                <div className="flex justify-between items-center p-2 sm:p-3 bg-crimson/10 border border-crimson/30">
                  <span className="text-gray-400 text-xs sm:text-sm">Points Earned</span>
                  <span className="text-lg sm:text-xl font-bold text-crimson">{stats?.rewards_earned || 0}</span>
                </div>
              </div>
            </motion.div>

            {/* Rewards Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-obsidian-light border border-[#222] p-4 sm:p-6"
            >
              <h3 className="font-heading text-base sm:text-lg font-bold mb-3 sm:mb-4 flex items-center gap-2">
                <Gift className="w-4 h-4 sm:w-5 sm:h-5 text-crimson" />
                Rewards
              </h3>
              <div className="grid grid-cols-2 lg:grid-cols-1 gap-2 sm:gap-3">
                <div className="p-2 sm:p-3 bg-[#111] border-l-2 border-crimson">
                  <p className="text-white font-medium text-sm">You Get</p>
                  <p className="text-crimson text-base sm:text-lg font-bold">+10 Points</p>
                  <p className="text-gray-500 text-xs">Per successful referral</p>
                </div>
                <div className="p-2 sm:p-3 bg-[#111] border-l-2 border-green-500">
                  <p className="text-white font-medium text-sm">Friend Gets</p>
                  <p className="text-green-400 text-base sm:text-lg font-bold">+5 Points</p>
                  <p className="text-gray-500 text-xs">When they complete profile</p>
                </div>
              </div>
            </motion.div>

            {/* Redeem Rewards CTA */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 }}
              className="bg-gradient-to-br from-crimson/20 to-crimson/5 border border-crimson/30 p-4 sm:p-6"
            >
              <h3 className="font-heading text-base sm:text-lg font-bold mb-2 flex items-center gap-2">
                <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5 text-crimson" />
                Redeem Points
              </h3>
              <p className="text-gray-400 text-xs sm:text-sm mb-3 sm:mb-4">
                Use your points to unlock premium features!
              </p>
              <Link to="/rewards" data-testid="redeem-rewards-link">
                <Button className="w-full bg-crimson hover:bg-crimson-hover text-sm">
                  View Rewards Catalog
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>

            {/* Leaderboard */}
            {leaderboard.length > 0 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-obsidian-light border border-[#222] p-4 sm:p-6"
              >
                <h3 className="font-heading text-base sm:text-lg font-bold mb-3 sm:mb-4 flex items-center gap-2">
                  <Trophy className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
                  Top Referrers
                </h3>
                <div className="space-y-1 sm:space-y-2">
                  {leaderboard.slice(0, 5).map((entry, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-2 sm:gap-3 p-1.5 sm:p-2"
                    >
                      <span className={`w-6 text-center font-bold text-xs sm:text-sm ${
                        idx === 0 ? "text-yellow-400" :
                        idx === 1 ? "text-gray-300" :
                        idx === 2 ? "text-orange-400" :
                        "text-gray-500"
                      }`}>
                        {idx === 0 ? "1st" : idx === 1 ? "2nd" : idx === 2 ? "3rd" : `#${idx + 1}`}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-xs sm:text-sm truncate">{entry.name}</p>
                      </div>
                      <span className="text-crimson font-bold text-xs sm:text-sm">
                        {entry.successful_referrals}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ReferralPage;
